import { Accordion, Button, Form, Modal } from 'react-bootstrap'
import JSONPretty from 'react-json-pretty'
import { CUSTOM_JSON_THEME } from '../../../common/common.constants'
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../../../common/common.entities'
import { ListingsInventory, ListingsInventoryMovements } from '../../utilities/listings.interfaces'
import { DateTime } from 'luxon'
import { useUserContext } from '../../../user/UserContext'
import { useCallback, useMemo, useState } from 'react'
import { apiFetch } from '../../../utility/fetch'
import { API_DEV_URL, API_PROD_URL } from '../../..'
import { getListingInventoryMovementsByInventoryId } from '../../utilities/listings.queries'
import ListingsInventoryMovementsModal from './ListingsInventoryMovementsModal'
import { SalesforceQueryResponse } from '../../../orders/orders.interfaces'
import { useLoading } from '../../../loading/LoadingContext'

interface ListingsInventoryModalProps {
  inventories: ListingsInventory[]
  modalVisible: boolean
  closeModal: any
  title: string
  environment: string
}

export default function ListingsInventoryModal(props: ListingsInventoryModalProps){
  if(!props.inventories) return <></>
  const {userData} = useUserContext()
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const [movementsModal, setMovementsModal]: [{visible: false, movements?: ListingsInventoryMovements[], title?: string}, any] = useState({visible: false, movements: undefined, title: undefined})

  const handleMovementClicked = useCallback(async (e) => {
    const {inventoryid} = e.currentTarget.dataset
    const relatedInventory = props.inventories.find(i => i.Id === inventoryid)
    changeStep('Loading Inventory Movements')
    changeSubStep(`Getting inventory movements for ${relatedInventory.Name}`)
    toggleLoading(true)
    const response = await apiFetch<SalesforceQueryResponse<ListingsInventoryMovements>>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: props.environment === 'production' ? API_PROD_URL : API_DEV_URL,
      body: JSON.stringify({
        soql: getListingInventoryMovementsByInventoryId(inventoryid)
      })
    })
    if(!response.ok){
      console.error(JSON.stringify(response.body))
      return
    }
    setMovementsModal({
      movements: response.body.records,
      title: `${relatedInventory.Name} Last 1000 Movements`,
      visible: true
    })
    toggleLoading(false)

  }, [props.inventories])

  const view = useMemo(() => {
    return <>
    <ListingsInventoryMovementsModal 
      closeModal={() => {setMovementsModal({visible: false})}}
      environment={props.environment}
      modalVisible={movementsModal?.visible}
      movements={movementsModal?.movements}
      title={movementsModal?.title}
    />
    <Modal show={props.modalVisible} animation={false} onHide={props.closeModal} className='modal-xl'>
    <Modal.Header 
      closeButton
      style={{ 
        position: 'sticky', 
        top: -1, 
        zIndex: 1050, 
        backgroundColor: 'white', 
        borderBottom: '1px solid #ddd'
      }}
    >
      <Modal.Title>
        <span className='beacon-header'>{props.title}</span>
      </Modal.Title>
    </Modal.Header>
      
    <Modal.Body>
      <div style={{fontSize: 'larger'}}>      
        <table className='flexport-table'>
          <thead>
            <tr className='sticky-row'>
              <th>
                <span className='beacon-header'>INVENTORY</span>
              </th>
              <th>
                <span className='beacon-header'>WAREHOUSE</span>
              </th>
              <th>
                <span className='beacon-header'>LIVE</span>
              </th>
              <th>
                <span className='beacon-header'>BUNDLE</span>
              </th>
              <th>
                <span className='beacon-header'>FULFILLABLE</span>
              </th>
              <th>
                <span className='beacon-header'>IN TRANSIT</span>
              </th>
              <th>
                <span className='beacon-header'>RESTOCK DATE</span>
              </th>
              <th>
                <span className='beacon-header'>LOOKUPS</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {props.inventories.map((i, index) => (
              <tr>
                <td>
                  <a href={`${SALESFORCE_URLS_BY_ENVIRONMENT[props.environment]}/${i.Id}`}  className='beacon-link' target="_blank" rel="noopener noreferrer">{i.Name}</a>
                </td>
                <td>
                  <a href={`${SALESFORCE_URLS_BY_ENVIRONMENT[props.environment]}/${i.Warehouse__c}`}  className='beacon-link' target="_blank" rel="noopener noreferrer">{i.Warehouse__r.Name}</a>
                </td>
                <td>
                  {i.Live_Inventory__c}
                </td>
                <td>
                  {i.Bundle_Inventory__c ?? 0}
                </td>
                <td>
                  {i.Fulfillable_Inventory__c}
                </td>
                <td>
                  {i.In_Transit_Total__c}
                </td>
                <td>
                  {i.Restock_Date2__c}
                </td>
                <td>
                  <Button onClick={handleMovementClicked} data-inventoryid={i.Id} variant='secondary'>MOVEMENTS</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button variant='secondary' onClick={props.closeModal}>CLOSE</Button>
    </Modal.Footer>
  </Modal>
  </>
  }, [props, movementsModal])

  return view
}